import React, { useEffect, useState } from 'react';
import { processingJobs as processingJobsAPI } from '@birdi/js-sdk/src/public';
import { css } from '@emotion/react';
import { blocks, breakpoints, buttonOutlineBlack, forms } from '@birdi/theme';
import { JobTable } from './JobTable';
import { Pagination } from '@birdi/pagination';
import { formating } from './utils';
import { downloadContent, objectToCsv } from '../../common/utils.js';
import {
  stroot,
  processingJobsV2 as ProcessingJobsV2API,
  organisation as organisationAPI,
} from '@birdi/js-sdk/src/public';
import { BirdiDatePicker } from '@birdi/date-picker';
import { useMetaSelector } from '../../meta/reducer';
import SearchSVG from '@birdi/icons/search.svg';
import { useNavigate, useLocation } from 'react-router';
import Select from 'react-select';

const LOAD_AMOUNT = 20;

interface Filters {
  searchTerm: string;
  dates: [string, string] | null;
}

export const ProcessingJobs: React.FC = () => {
  const meta = useMetaSelector();
  const [orgName, setOrgName] = useState('');

  const [jobs, setJobs] = useState(null);
  const [filters, setFilters] = useState<Filters>({
    searchTerm: '',
    dates: null,
  });

  const [start, setStart] = useState(1);
  const [count, setCount] = useState(0);
  const [mapVersion, setMapVersion] = useState({
    value: '2.0 Maps',
    label: '2.0 Maps',
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;
  let workspaceId = null;
  if (pathname.includes('workspace-processing')) {
    workspaceId = pathname.substring(pathname.lastIndexOf('/') + 1);
    organisationAPI.getOneById(stroot('iy3ohb'), workspaceId).then((res) => {
      setOrgName(res.body.name);
    });
  }

  const searchJobs = (filters: Filters, start: number = 1) => {
    mapVersion.value === '2.0 Maps'
      ? ProcessingJobsV2API.getJobsV2(stroot('mool3u'), {
          start,
          limit: LOAD_AMOUNT,
          processingJobName: filters.searchTerm,
          ...(filters.dates && { dates: filters.dates }),
          ...(workspaceId && { workspaceId }),
        }).then((res) => {
          setJobs(res.body.items);
          setCount(res.body.count);
        })
      : processingJobsAPI
          .getJobs(stroot('9vp5go'), {
            start,
            limit: LOAD_AMOUNT,
            mapName: filters.searchTerm,
            ...(filters.dates && { dates: filters.dates }),
            ...(workspaceId && { workspaceId }),
          })
          .then((res) => {
            setJobs(res.body.items);
            setCount(res.body.count);
          });
  };

  useEffect(() => {
    searchJobs(filters);
  }, []);

  const updatePage = (page: number) => {
    setStart(page);
    searchJobs(filters, page);
  };

  const updateFilters = (updatedFilter: Partial<Filters>) => {
    setFilters({ ...filters, ...updatedFilter });
    setStart(1);
    searchJobs({ ...filters, ...updatedFilter });
  };

  useEffect(() => {
    setStart(1);
    searchJobs(filters);
  }, [mapVersion]);

  const downloadData = async () => {
    const headers = [
      {
        label:
          mapVersion.value === 'Legacy Maps'
            ? 'Map Name'
            : 'Processing Job Name',
        key: 'name',
      },
      ...(mapVersion.value === 'Legacy Maps'
        ? []
        : [{ label: 'Map Name', key: 'mapName' }]),
      { label: 'Status', key: 'status' },
      { label: 'GP', key: 'gp' },
      { label: 'Requested By', key: 'requestedBy' },
      { label: 'Created Date', key: 'createdDate' },
    ];
    if (jobs && jobs.length > 0) {
      const data = jobs.map((job) => formating(job));
      const result = await objectToCsv(headers, data);
      downloadContent('processing_jobs.csv', result, 'text/csv');
    }
  };

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>
          Processing &nbsp;&nbsp;
          <span
            css={(theme) => css`
              color: ${theme.mono40};
            `}
          >
            {orgName}
          </span>
        </h2>
      </div>
      <div
        css={(theme) => css`
          ${blocks.pageGraySection(theme)};
          justify-content: space-between;
          position: relative;
          // z-index: 3;
          @media (max-width: ${breakpoints.xs}) {
            padding-bottom: 1em;
            p {
              margin-bottom: 0;
            }
          }
        `}
      >
        <p>
          {!workspaceId
            ? 'View, filter and export your processing jobs.'
            : "View, filter and export your workspace's processed jobs."}
        </p>
        {count > 0 ? (
          <button
            type="button"
            css={(theme) => css`
              ${buttonOutlineBlack(theme)};
              background-color: ${theme.mono0};
              border: 1px solid ${theme.mono40};
              border-radius: 4px;
              height: 2em;
              span {
                color: ${theme.darkGrey};
              }
            `}
            disabled={!jobs || jobs.length === 0}
            onClick={async () => {
              await downloadData();
            }}
          >
            <span>Export</span>
          </button>
        ) : (
          <></>
        )}
      </div>

      <div
        css={(theme) => css`
          ${blocks.separator2(theme)}
        `}
      >
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
          }}
          css={css`
            flex-grow: 1;
            display: flex;
            flex-wrap: wrap;
            & > *:not(last-child) {
              margin-right: 0.5em;
            }
            gap: 0.5em;
            @media (max-width: ${breakpoints.xs}) {
              flex-direction: column;
            }
          `}
        >
          <Select
            isMulti={false}
            value={mapVersion}
            options={[
              {
                value: '2.0 Maps',
                label: '2.0 Maps',
              },
              {
                value: 'Legacy Maps',
                label: 'Legacy Maps',
              },
            ]}
            onChange={(option) => {
              if (option) setMapVersion(option);
            }}
            styles={{
              ...forms.reactSelect,
              container: (base) => ({
                ...base,
                width: '20em',
              }),
            }}
          />
          <div
            css={css`
              position: relative;
              display: inline-block;
            `}
          >
            <SearchSVG
              css={(theme) => css`
                position: absolute;
                top: 50%;
                left: 10px;
                transform: translateY(-50%);
                pointer-events: none;
                height: 1em;
                width: 1em;
                color: ${theme.mono40};
              `}
            />
            <input
              id="mapName"
              type="text"
              value={filters.searchTerm}
              placeholder="Search by name"
              onChange={(event) =>
                updateFilters({ searchTerm: event.target.value })
              }
              css={(theme) => css`
                ${blocks.input(theme)};
                // border: 1px solid #ccc !important;
                min-width: 20em;
                padding-left: 2em;
                &::placeholder {
                  font-size: 1em;
                }
              `}
            />
          </div>
          <BirdiDatePicker
            type="range"
            onChange={(dates) => updateFilters({ dates: dates })}
            defaultStartDate={null}
            defaultEndDate={null}
            placeholder="Filter by created dates"
          />
        </form>
      </div>

      <section
        css={css`
          padding: 1em 1em 2em 1em;
        `}
      >
        <JobTable jobs={jobs} />
        <div
          css={css`
            padding: 1em 0;
          `}
        >
          <Pagination
            onChange={(currentPage) => updatePage(currentPage)}
            itemCount={count}
            itemsPerPage={LOAD_AMOUNT}
            currentPage={start}
          />
        </div>
      </section>
    </div>
  );
};

export default ProcessingJobs;
